import { abyssColors } from '@/theme/colors';
import { Edit, RestartAlt } from '@mui/icons-material';
import {
  Button,
  Checkbox,
  FormControlLabel,
  Grid2 as Grid,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useAtom } from 'jotai';
import { useState } from 'react';
import { selectedPOIType } from '../../state';
import { FullWidthPanel } from './styles';

const STATUS_FIELDS = [
  {
    key: 'inprogress',
    defaultName: 'In Progress',
    description: 'POI is currently being worked on',
  },
  {
    key: 'completed',
    defaultName: 'Completed',
    description: 'POI has been fully resolved',
  },
  {
    key: 'inreview',
    defaultName: 'In Review',
    description: 'POI is awaiting review or approval',
  },
  {
    key: 'chaseItemAdded',
    defaultName: 'Chase Item Added',
    description: 'A chase item has been raised for this POI',
  },
  {
    key: 'partiallyCompleted',
    defaultName: 'Partially Completed',
    description: 'POI has been partially resolved',
  },
  {
    key: 'remediated',
    defaultName: 'Remediated',
    description: 'POI has been remediated',
  },
];

type StatusFieldDetails = { name?: string | null; hidden?: boolean | null } | null | undefined;
type StatusConfig = Record<string, StatusFieldDetails>;

export const PoiStatusConfigPanel = ({
  handleSaveTemplate,
}: {
  handleSaveTemplate: () => void;
}) => {
  const [selectedType, setSelectedType] = useAtom(selectedPOIType);
  const [mode, setMode] = useState<'ReadOnly' | 'Edit'>('ReadOnly');

  const getStatusConfig = (): StatusConfig => {
    return (selectedType?.statusConfig as StatusConfig) ?? {};
  };

  const getFieldValue = (fieldKey: string, defaultName: string): string => {
    return (getStatusConfig()[fieldKey] as { name?: string | null })?.name ?? defaultName;
  };

  const getFieldHidden = (fieldKey: string): boolean => {
    return (getStatusConfig()[fieldKey] as { hidden?: boolean | null })?.hidden ?? false;
  };

  const handleFieldNameChange = (fieldKey: string, newName: string) => {
    const current = getStatusConfig()[fieldKey] as StatusFieldDetails;
    setSelectedType({
      ...selectedType,
      statusConfig: {
        ...getStatusConfig(),
        [fieldKey]: {
          ...current,
          name: newName,
        },
      },
    });
  };

  const handleFieldHiddenChange = (fieldKey: string, hidden: boolean) => {
    const current = getStatusConfig()[fieldKey] as StatusFieldDetails;
    setSelectedType({
      ...selectedType,
      statusConfig: {
        ...getStatusConfig(),
        [fieldKey]: {
          ...current,
          hidden,
        },
      },
    });
  };

  const handleResetField = (fieldKey: string) => {
    const updatedConfig = { ...getStatusConfig() };
    delete updatedConfig[fieldKey];
    setSelectedType({
      ...selectedType,
      statusConfig: updatedConfig,
    });
  };

  return (
    <>
      <Stack
        width="100%"
        marginTop="1.2rem"
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        height="4.8rem"
      >
        <Typography fontSize="2.2rem" fontWeight={500}>
          {`Status Configuration for ${selectedType?.name}`}
        </Typography>
        {mode === 'Edit' ? (
          <Stack direction="row" spacing={1} sx={{ pb: 0.5 }}>
            <Button
              variant="contained"
              onClick={() => {
                setMode('ReadOnly');
                handleSaveTemplate();
              }}
            >
              Save
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                setMode('ReadOnly');
              }}
            >
              Cancel
            </Button>
          </Stack>
        ) : (
          <Stack direction="row" spacing={0} sx={{ pb: 0.5 }}>
            <Tooltip title="Edit Status Configuration" placement="top" arrow>
              <IconButton size="medium" onClick={() => setMode('Edit')}>
                <Edit
                  fontSize="inherit"
                  sx={{ cursor: 'pointer', color: abyssColors.primary[500] }}
                />
              </IconButton>
            </Tooltip>
          </Stack>
        )}
      </Stack>
      <FullWidthPanel>
        <Stack sx={{ flex: 1, gap: '3.2rem', width: '100%' }}>
          <Stack gap={2}>
            <Typography variant="h4">Customize POI Status Labels</Typography>
            <Typography variant="body2" color="text.secondary">
              Override the display names and visibility for POI statuses on this template. These
              settings take precedence over the deployment-level status labels. Hidden statuses will
              not appear in status dropdowns.
            </Typography>
          </Stack>

          <Stack gap={3} width="100%">
            {STATUS_FIELDS.map((field) => {
              const currentValue = getFieldValue(field.key, field.defaultName);
              const isHidden = getFieldHidden(field.key);
              const fieldConfig = getStatusConfig()[field.key] as StatusFieldDetails;
              const isModified =
                fieldConfig?.name !== undefined || fieldConfig?.hidden !== undefined;

              return (
                <Grid
                  container
                  key={field.key}
                  spacing={2}
                  alignItems="center"
                  sx={{
                    padding: 2,
                    borderRadius: 1,
                    backgroundColor: isModified ? abyssColors.primary[50] : abyssColors.neutral[50],
                    border: `1px solid ${
                      isModified ? abyssColors.primary[200] : abyssColors.neutral[200]
                    }`,
                    opacity: isHidden ? 0.6 : 1,
                  }}
                >
                  <Grid size={3}>
                    <Stack gap={0.5}>
                      <Typography variant="h6" fontWeight={500}>
                        {field.defaultName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {field.description}
                      </Typography>
                    </Stack>
                  </Grid>
                  <Grid size={5}>
                    <TextField
                      fullWidth
                      disabled={mode === 'ReadOnly' || isHidden}
                      value={currentValue}
                      onChange={(event) => handleFieldNameChange(field.key, event.target.value)}
                      placeholder={field.defaultName}
                      variant="outlined"
                      size="small"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: 'white',
                        },
                      }}
                    />
                  </Grid>
                  <Grid size={2}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={isHidden}
                          onChange={(event) =>
                            handleFieldHiddenChange(field.key, event.target.checked)
                          }
                          disabled={mode === 'ReadOnly'}
                          size="small"
                        />
                      }
                      label={
                        <Typography variant="body2" color="text.secondary">
                          Hidden
                        </Typography>
                      }
                    />
                  </Grid>
                  <Grid size={2}>
                    <Button
                      variant="outlined"
                      size="small"
                      disabled={mode === 'ReadOnly' || !isModified}
                      startIcon={<RestartAlt />}
                      onClick={() => handleResetField(field.key)}
                      sx={{
                        color: abyssColors.neutral[600],
                        borderColor: abyssColors.neutral[300],
                        '&:hover': {
                          borderColor: abyssColors.neutral[400],
                          backgroundColor: abyssColors.neutral[100],
                        },
                      }}
                    >
                      Reset
                    </Button>
                  </Grid>
                </Grid>
              );
            })}
          </Stack>

          <Stack
            gap={1}
            padding={2}
            sx={{
              backgroundColor: abyssColors.primary[50],
              borderRadius: 1,
              border: `1px solid ${abyssColors.primary[200]}`,
            }}
          >
            <Typography variant="h6" color={abyssColors.primary[700]}>
              Note
            </Typography>
            <Typography variant="body2" color={abyssColors.primary[700]}>
              Template-level status configuration takes precedence over deployment-level labels.
              When no template config is set, the deployment-level labels are used as a fallback.
            </Typography>
          </Stack>
        </Stack>
      </FullWidthPanel>
    </>
  );
};
